import { defineStore } from 'pinia'
import { useApi, useFlow } from '@/shared/composables'
import type {
  GetPolicyResponse,
  PromocodeProductType,
  PromocodeTransactionType,
  SendFormDataResult,
  Relation,
  UaeWording,
  FileCategory,
  UaeFAQ,
  Nationality,
} from '@/shared/types/api.types'
import type { State } from './commonStore.types'
import { CONTACTS_KEYS, TravelDirection } from '@/shared/types/common.types'
import { CountryBoundNumber, WordingDocName } from '@/shared/types/api.types'
import getPolicy from '@/shared/tests/mocks/responses/getpolicy.json'
import { useUserStore } from '../userStore/userStore'
import { A_B_KEY } from '@/shared/constants'

const getDefaultAbHash = () => {
  let hash: Record<string, string> = {}

  const existingState = localStorage.getItem(A_B_KEY)
  if (existingState) {
    let parsed
    try {
      parsed = JSON.parse(existingState)
    } catch {
      parsed = existingState
    }

    // Save old quote a/b test value
    if (typeof parsed === 'string') {
      hash = {}
    } else {
      hash = parsed
    }
  }

  return hash
}

export const useCommonStore = defineStore('commonStore', {
  state: (): State => ({
    nationalities: [],
    relations: [],
    isStopBack: false,
    backCallback: null,
    policyUrl: undefined,
    contacts: [],
    abHash: getDefaultAbHash(),
  }),
  getters: {
    getContactValue:
      (state) =>
      (key: CONTACTS_KEYS): string => {
        const contact = state.contacts.find((el) => el.ContactKey === key)
        if (!contact) {
          return ''
        }

        return contact.Value
      },
    getAbValue:
      (state) =>
      (code: string): string => {
        const variant = state.abHash[code] || 'default'
        return variant
      },
  },
  actions: {
    async sendFormData(params: any): Promise<SendFormDataResult> {
      const api = useApi()
      //@ts-ignore
      const { flowDirection } = useFlow(this.$router.currentRoute)
      const additionalParams =
        flowDirection === TravelDirection.UNKNOWN
          ? {}
          : {
              boundType:
                flowDirection === TravelDirection.INBOUND
                  ? CountryBoundNumber.INBUND
                  : CountryBoundNumber.OUTBUND,
            }
      return await api.commonService.sendFormData({
        ...params,
        ...additionalParams,
      })
    },
    async validatePromocode(promocode: string, type: PromocodeProductType): Promise<boolean> {
      const transactionType: PromocodeTransactionType = 'POL'
      try {
        const api = useApi()
        const promocodes = await api.commonService.validatePromocode(promocode)
        const usage: (typeof promocodes)[0] | undefined = promocodes.find(
          (usage) => usage.transactionType === transactionType && usage.productType === type
        )
        const userStore = useUserStore()
        userStore.setAffinity(usage?.isAffinity === 1)
        return !!usage && new Date(Number(usage.expirationDate)) >= new Date()
      } catch (e) {
        return false
      }
    },
    validateEmail(email: string): Promise<string> {
      const api = useApi()
      return api.commonService.validateEmail(email)
    },
    // TODO: need to save in store all list of wordings to avoid several requests to server at one time. And get needed wording using filter.
    async getQatarWordings(
      categoryid: FileCategory,
      docname: WordingDocName
    ): Promise<UaeWording[]> {
      const api = useApi()
      return await api.commonService.getQatarWordings(categoryid, docname)
    },

    async getPrivacyPolicyUrl() {
      if (!this.policyUrl) {
        const urls = await this.getQatarWordings(1, WordingDocName.PRIVACY_POLICY)
        this.policyUrl = urls[0].Link
      }

      return this.policyUrl
    },

    async getUaeFAQs(categoryid: FileCategory): Promise<UaeFAQ[]> {
      const api = useApi()
      return await api.commonService.getUaeFAQs(categoryid)
    },

    //TODO: Extract into other store

    async getPolicy(): Promise<GetPolicyResponse> {
      if (!import.meta.env.PROD) return Promise.resolve(getPolicy)

      const api = useApi()
      return await api.commonService.getPolicy()
    },

    async findNationalities(query?: string): Promise<Nationality[]> {
      const api = useApi()
      return await api.commonService.findNationalities(query)
    },

    setNationalities(nationalities: Nationality[]): void {
      this.nationalities = nationalities
    },

    async getRelations(): Promise<Relation[]> {
      const api = useApi()
      return await api.commonService.getRelations()
    },

    setRelations(relations: Relation[]): void {
      this.relations = relations
    },
    async sendCallback(phone: string) {
      const api = useApi()
      return await api.commonService.sendCallback(phone)
    },

    setStopBack(isStopBack: boolean): void {
      this.isStopBack = isStopBack
    },

    setBackCallback(backCallback: (() => void) | null): void {
      this.backCallback = backCallback
    },

    async getAffinityData(code: string) {
      const api = useApi()
      return await api.commonService.getAffinityDate(code)
    },

    getPaymentOptions(quote: string, locale: string) {
      const api = useApi()
      return api.commonService.getPaymentOptions(quote, locale)
    },

    async getAbTest(code: string): Promise<string | undefined> {
      if (this.abHash[code]) {
        return
      }

      let newType = ''

      try {
        const response = await useApi().commonService.getAbDistribution<string>(code)
        newType = response.find((item) => item.experiment === code)?.group || 'default'
      } catch {
        newType = 'default'
      } finally {
        this.abHash[code] = newType
        localStorage.setItem(A_B_KEY, JSON.stringify(this.abHash))
      }

      return newType
    },

    async getContacts() {
      if (this.contacts.length) {
        return this.contacts
      }

      const data = await useApi().commonService.getContacts()
      this.contacts = data
    },

    async getConfig() {
      return await useApi().commonService.getConfig()
    },
  },
})
